import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import styles from '../../share-panel.module.scss';
import {Dropdown} from '@Components/dropdown-v2';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import type {ShareAccessOptionsDropdownProps} from '@Panels/share-panel/components/share-access-options-dropdown/share-access-options-dropdown.types';
import {useModalData} from '@Components/modal/hooks/useModalData';
import {MODALS_IDS} from '@Components/modal-container';
import {updatePanelPropsForModal} from '@Components/modal-container/modal-container-reducer';
import {useAppDispatch} from '@/hooks';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {closePanel} from '@Components/panel/panel-reducer';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';

export function ShareAccessOptionsDropdown({...props}: ShareAccessOptionsDropdownProps): ReactElement<any> {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const modalData = useModalData(MODALS_IDS.MORE_OPTIONS_MODAL);
  const dispatch = useAppDispatch();

  const getVerticalDropdownBorder = (): ReactElement<any> => {
    return <div className={`${styles.verticalBorder} spacing-m-r-2 spacing-m-l-2`}></div>;
  };

  useEffect(() => {
    if (!modalData) {
      return;
    }

    dispatch(
      updatePanelPropsForModal({
        modalId: MODALS_IDS.MORE_OPTIONS_MODAL,
        panelProps: {
          ...modalData.panelProps,
          content: props.accessDropdownOptions,
        },
      })
    );
  }, [props.permissionLevelLabel]);

  const onClick = (): void => {
    if (modalData?.panelProps.panelId && props.doCloseOnInnerClick) {
      dispatch(closePanel(modalData?.panelProps.panelId as string));
    }
  };

  const getPopup = (): ReactElement<any> => {
    return props.doCloseOnInnerClick ? (
      <ClickableDiv onClick={onClick} className="_full-width">
        {props.accessDropdownOptions}
      </ClickableDiv>
    ) : (
      props.accessDropdownOptions
    );
  };

  const getSelector = (): ReactElement<any> => {
    if (props.showTooltip) {
      return (
        <div>
          <OverlayTrigger
            overlay={<Tooltip className="spacing-m-b-2 _rounded-arrow">{props.tooltipText}</Tooltip>}
            trigger={['hover', 'focus']}
            key={`tooltip-share-options-dropdown`}
          >
            <div>
              <DropdownDefaultSelectBox
                text={props.permissionLevelLabel}
                textClassName="body-xs-bold flex-1 ct content-body"
                className={`${props.popupSelectorClassName} content-body _borderless ${isDropdownOpen ? styles.open : ''}`}
              />
            </div>
          </OverlayTrigger>
        </div>
      );
    }

    return (
      <DropdownDefaultSelectBox
        text={props.permissionLevelLabel}
        textClassName="body-xs-bold flex-1 ct content-body"
        className={`${props.popupSelectorClassName} content-body _borderless ${isDropdownOpen ? styles.open : ''}`}
      />
    );
  };

  const getDropdown = (): ReactElement<any> => {
    return (
      <Dropdown
        selector={getSelector()}
        popup={getPopup()}
        doCloseOnInnerClick={props.doCloseOnInnerClick}
        doesCloseOnOuterClick
        popupClassName={`border-s-standard radius-8 ${props.popupClassName}`}
        popUpHasCustomWidth
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        showModalForSmallScreen
        onOpen={() => {
          setIsDropdownOpen(true);
        }}
        onClose={() => {
          setIsDropdownOpen(false);
        }}
        position={DROPDOWN_POSITION.FIXED}
        panelIdForModal={'share-options-dropdown'}
        modalTitle={props.doCloseOnInnerClick ? window.i18next.t('pmwjs_change_permission') : window.i18next.t('pmwjs_change_permissions')}
        className="spacing-p-r-1 flex-center"
        popupOffset={8}
      />
    );
  };

  return (
    <div className={`${styles.dropdownContainer} flex-h-row`}>
      {getVerticalDropdownBorder()}
      {getDropdown()}
    </div>
  );
}
